// Kanban pipeline (audit #13) — drag-and-drop board over the existing
// leads array. Reads/writes lead.status via setLeads passed in from
// CommissionTracker root. Pure client-side — no new backend.
//
// Columns mirror the standard insurance pipeline stages.
import React from 'react';

const COLUMNS = [
  { id: 'new',          label: 'New',         tone: 'bg-slate-50 border-slate-200' },
  { id: 'contacted',    label: 'Contacted',   tone: 'bg-sky-50 border-sky-200' },
  { id: 'appt_set',     label: 'Appt Set',    tone: 'bg-amber-50 border-amber-200' },
  { id: 'showed',       label: 'Showed',      tone: 'bg-violet-50 border-violet-200' },
  { id: 'closed',       label: 'Closed',      tone: 'bg-emerald-50 border-emerald-200' },
  { id: 'recycled',     label: 'Recycled',    tone: 'bg-rose-50 border-rose-200' },
];

export default function KanbanPipelineView({ leads = [], setLeads = () => {}, showToast = () => {}, onGotoTab = () => {} }) {
  const [drag, setDrag] = React.useState(null);   // { leadId }
  const [filter, setFilter] = React.useState('all'); // 'all' | 'life' | 'health'

  const visibleLeads = (leads || []).filter(l => {
    if (filter === 'all') return true;
    const v = l.vertical || (l.sector === 'medicare' || l.sector === 'aca_private' ? 'health' : 'life');
    return v === filter;
  });

  const move = (leadId, toStatus) => {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: toStatus, statusChangedAt: new Date().toISOString() } : l));
    showToast(`Moved to ${toStatus}`);
  };

  const byCol = COLUMNS.reduce((acc, c) => { acc[c.id] = visibleLeads.filter(l => (l.status || 'new') === c.id); return acc; }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Pipeline · Kanban</h1>
          <p className="text-sm text-slate-500">Drag leads between stages. {visibleLeads.length} of {leads.length} shown.</p>
        </div>
        <div className="flex gap-1">
          {[['all','All'],['life','Life'],['health','Health']].map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border-2 ${filter === id ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-700'}`}>{label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {COLUMNS.map(col => (
          <div
            key={col.id}
            onDragOver={e => e.preventDefault()}
            onDrop={() => { if (drag) { move(drag, col.id); setDrag(null); } }}
            className={`border-2 ${col.tone} rounded-lg p-2 min-h-[300px]`}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 px-1 pb-2 flex items-center justify-between">
              {col.label}
              <span className="tabular-nums text-slate-500">{byCol[col.id].length}</span>
            </div>
            <div className="space-y-2">
              {byCol[col.id].slice(0, 50).map(lead => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={() => setDrag(lead.id)}
                  onDragEnd={() => setDrag(null)}
                  className={`bg-white border border-slate-200 rounded-md px-2 py-1.5 cursor-grab text-xs hover:border-slate-400 ${drag === lead.id ? 'opacity-50' : ''}`}
                >
                  <div className="font-bold text-slate-900 truncate">{lead.name || 'Unnamed'}</div>
                  <div className="text-slate-500 text-[11px]">{lead.phone || lead.email || '(no contact)'}</div>
                  {lead.leadType && lead.leadType !== 'general' && (
                    <div className="text-[10px] mt-0.5 uppercase tracking-wider text-slate-600">{String(lead.leadType).replace(/_/g, ' ')}</div>
                  )}
                </div>
              ))}
              {byCol[col.id].length === 0 && (
                <div className="text-[11px] text-slate-400 italic text-center py-4">drop here</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
